import { z } from 'zod';
import { FeedKindSchema } from './kind.js';
import { FeedListJobKindSchema } from './listJob.js';
import { FeedCalendarSyncJobParamsSchema, FeedOutreachSyncJobParamsSchema, } from './listJobConfig.js';
import { FEED_POLLING_DAYS_OF_WEEK, FEED_POLLING_HOURS, FEED_POLLING_INTERVAL_MINUTES, FEED_POLLING_ORDINALITIES, } from './pollingSchedule.js';
/**
 * When a pulled feed goes looking for rows, as a read describes it.
 *
 * The write schema states each field as the set of values the staff form offers, which a read
 * cannot assume of a schedule stored before that form or written by hand: one `intervalMinutes: 45`
 * would fail the whole feed read. These are plain numbers, described by the sets a schedule
 * normally holds, so an unusual schedule is reported rather than hidden behind an error.
 */
const FeedReadPollingScheduleSchema = z.object({
    daysOfWeek: z.array(z.object({
        day: z
            .number()
            .int()
            .describe(`Practice-local weekday: Sunday 0 through Saturday 6. Normally one of ${FEED_POLLING_DAYS_OF_WEEK.join(', ')}.`),
        ordinality: z
            .number()
            .int()
            .optional()
            .describe(`Which occurrence of the weekday in the month, normally one of ${FEED_POLLING_ORDINALITIES.join(', ')}.`),
    })),
    firstHour: z
        .number()
        .int()
        .describe(`First eligible hour, inclusive, in the first practice's local timezone. Normally one of ${FEED_POLLING_HOURS.join(', ')}.`),
    lastHour: z
        .number()
        .int()
        .describe(`Last eligible hour, inclusive, in the first practice's local timezone. Normally one of ${FEED_POLLING_HOURS.join(', ')}.`),
    intervalMinutes: z
        .number()
        .int()
        .describe(`Poll when minutes since local midnight are divisible by this interval and the day and hour are eligible; not an interval measured from firstHour or the last run. A 1440-minute interval matches only midnight, so a window excluding midnight has no eligible poll. Normally one of ${FEED_POLLING_INTERVAL_MINUTES.join(', ')}.`),
});
/**
 * Parameters for the two jobs that take them.
 *
 * Rebuilt from each write schema's fields rather than reused, because those are strict: one key
 * from an older job would fail both variants and reduce a calendar sync to no parameters at all.
 * These strip what they do not name instead, so an unrecognized key costs the key. Parameters
 * matching neither job, which no current code writes, still fall through to the empty object.
 */
const FeedReadJobParamsSchema = z.union([
    z.object(FeedCalendarSyncJobParamsSchema.shape),
    z.object(FeedOutreachSyncJobParamsSchema.shape),
    z.object({}),
]);
/**
 * The stored configuration as a read describes it: when the job runs and what it is parameterized
 * with, and nothing else.
 *
 * Named field by field rather than reusing `FeedListJobConfigSchema`, which states what a write
 * must be. That schema also carries `sftpParams`, the server and path a pushed feed lands on,
 * which primary serves from its own endpoint behind the `ViewFeedConfigs` scope and no read of the
 * feed should hand out. Every other key, declared or legacy, is dropped rather than failing the
 * read.
 */
const FeedReadListJobConfigSchema = z
    .object({
    pollingSchedule: FeedReadPollingScheduleSchema.optional(),
    jobParams: FeedReadJobParamsSchema.optional(),
})
    .nullable();
/**
 * One feed: where its rows come from, which practices they land in, and the job that pulls them.
 *
 * Configuration only. A feed's rows carry patient data, but the feed itself does not, and the
 * connection credentials behind a pushed feed are served separately under their own scope.
 */
export const FeedSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    organizationId: z
        .uuid()
        .nullable()
        .describe('The organization owning the feed. Null for a feed configured before ownership.'),
    practiceIds: z.array(z.uuid()).describe('The practices whose rows arrive on this feed.'),
    kind: FeedKindSchema.describe('EHR_PUSH for rows an EHR sends us; SYNC_LIST for SFTP files, RPA/FHIR fetches, manual uploads, and inbound data streams.'),
    enabled: z
        .boolean()
        .describe('Whether automated RPA/FHIR file/batch fetching is enabled. Not a master feed switch: SFTP polling is configured separately and can run when this is false. This response does not expose SFTP configuration, so false does not establish that the feed is inactive.'),
    ingestEnabled: z
        .boolean()
        .describe('Whether the inbound data-stream ingest endpoint accepts pushed rows. Does not control SFTP polling, RPA/FHIR fetching, or manual uploads; those do not require this flag to be true.'),
    itemTrackingEnabled: z
        .boolean()
        .describe('Whether each row is tracked as a feed item that can be inspected individually.'),
    listJobKind: FeedListJobKindSchema.nullable().describe('What a pulled row means: an appointment update, a calendar sync, outreach, a patient list, or patient orders.'),
    listJobConfig: FeedReadListJobConfigSchema.describe('Saved polling schedule and job parameters, not evidence of recent or future execution. SFTP configuration is omitted; this response cannot determine whether SFTP polling is enabled. Null means no list-job configuration is stored.'),
    timeZone: z
        .string()
        .nullable()
        .describe("The IANA time zone the polling schedule's hours are read in, which is the time zone of the feed's first practice. Null when the feed serves no practice."),
    deletedTimestamp: z.iso.datetime().nullable(),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
});
/**
 * How many expected columns one feed reports before the rest are dropped.
 *
 * The configured ones come first and the canonical fields for the feed kind follow, so a feed
 * configured past this bound loses the fields it never mapped rather than the ones it did.
 */
export const FEED_EXPECTED_SOURCE_FIELD_LIMIT = 1_000;
/**
 * The source columns a feed's rows must supply: those its cell pipelines and field mappings name,
 * then every canonical field for its kind that nothing maps.
 */
export const FeedExpectedSourceFieldsSchema = z.strictObject({
    fields: z
        .array(z.string())
        .max(FEED_EXPECTED_SOURCE_FIELD_LIMIT)
        .describe('Column names the incoming rows must supply, the ones this feed configures first and the canonical fields for its kind after them.'),
    truncation: z
        .literal('fieldLimit')
        .nullable()
        .describe('Null when every expected column fit, which is all but a pathological feed.'),
});
//# sourceMappingURL=read.js.map